// ============================================================================
// PROTECTED ROUTE — Route-level authentication + module access guard
// ============================================================================
// Wraps routes that require authentication and optionally a specific module.

import React, { type ReactNode } from 'react';
import { Navigate } from 'react-router-dom';
import { useAuthContext } from '../../contexts/AuthContext';
import { tokenManager } from '../../auth/tokenManager';
import { STORAGE_KEYS } from '../../auth/storageKeys';

interface ProtectedRouteProps {
  children: ReactNode;
  /** Require access to a specific module */
  moduleCode?: string;
  /** Redirect target when not authenticated (default: '/') */
  redirectTo?: string;
  /** Loading component to show while checking auth */
  loadingComponent?: ReactNode;
}

const getAuthMode = (): 'tenant-hub' | 'legacy' => {
  return (process.env.REACT_APP_AUTH_MODE as 'tenant-hub' | 'legacy') || 'legacy';
};

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  moduleCode,
  redirectTo = '/',
  loadingComponent,
}) => {
  const { isAuthenticated, isLoading, permissionEngine } = useAuthContext();

  // Legacy mode: check tokenManager + stored user directly. AuthContext's
  // isAuthenticated always returns true in legacy mode (line 132-138), so we
  // must inspect the same signals LoginInterface's auto-redirect uses. Without
  // this guard, browser Back after logout re-enters the dashboard from history.
  if (getAuthMode() !== 'tenant-hub') {
    const hasValidToken = tokenManager.isAuthenticated();
    const hasStoredUser = !!localStorage.getItem(STORAGE_KEYS.USER);
    if (!hasValidToken || !hasStoredUser) {
      return <Navigate to={redirectTo} replace />;
    }
    return <>{children}</>;
  }

  if (isLoading) {
    return <>{loadingComponent ?? <div>Loading...</div>}</>;
  }

  if (!isAuthenticated) {
    return <Navigate to={redirectTo} replace />;
  }

  if (moduleCode && !permissionEngine.hasModuleAccess(moduleCode)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
